import { toast } from 'react-toastify';
import {
    Order,
    OrderFilterParams,
    OrderSearchParams,
    PlaceOrderBody,
    OrderResponse,
    ScanProductBody,
    UpdateOrderQuantityBody,
} from '@core/models/order.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import OrderService from 'services/api/order.service';

// -------------------------------------------------------------------------

interface IOrderState {
    loading: boolean;
    error: IErrorModel | undefined;
    orders: Order[];
    order: Order | null;
    pagination: {
        pageNumber: number;
        pageSize: number;
        totalPages: number;
        totalElements: number;
    };
    placeOrderLoading: boolean;
    getOrderByIdLoading: boolean;
    filterOrderLoading: boolean;
    searchOrderLoading: boolean;
    exportOrdersLoading: boolean;
    createNewToteLoading: boolean;
    scanProductLoading: boolean;
    closeToteLoading: boolean;
    shipToteLoading: boolean;
    approveToteLoading: boolean;
    updateQuantityOrderLoading: boolean;
    cancelOrderLoading: boolean;
}

const initialState: IOrderState = {
    loading: false,
    error: {} as IErrorModel,
    orders: [],
    order: null,
    pagination: {
        pageNumber: 0,
        pageSize: 10,
        totalPages: 0,
        totalElements: 0,
    },
    placeOrderLoading: false,
    getOrderByIdLoading: false,
    filterOrderLoading: false,
    searchOrderLoading: false,
    exportOrdersLoading: false,
    createNewToteLoading: false,
    scanProductLoading: false,
    closeToteLoading: false,
    shipToteLoading: false,
    approveToteLoading: false,
    updateQuantityOrderLoading: false,
    cancelOrderLoading: false,
};

// -------------------------------------------------------------------------

export const placeOrderThunk = createAsyncThunk<ISuccessResponse, PlaceOrderBody, { rejectValue: IErrorModel }>(
    `/order/placeOrder`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.placeOrder(body);
            toast.success(response.data.message || 'Order placed successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to place order', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getOrderByIdThunk = createAsyncThunk<any, number, { rejectValue: IErrorModel }>(
    `/order/getOrderById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.getOrderById(id);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch order', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const filterOrderThunk = createAsyncThunk<
    any,
    OrderFilterParams | undefined,
    { rejectValue: IErrorModel }
>(`/order/filterOrder`, async (params, { rejectWithValue }: any) => {
    try {
        const response: any = await OrderService.filterOrder(params);
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to filter orders', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const searchOrderThunk = createAsyncThunk<
    OrderResponse,
    OrderSearchParams | undefined,
    { rejectValue: IErrorModel }
>(`/order/searchOrder`, async (params, { rejectWithValue }: any) => {
    try {
        const response: any = await OrderService.searchOrder(params);
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to search orders', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const exportOrdersThunk = createAsyncThunk<
    any,
    { startDate: string, endDate: string },
    { rejectValue: IErrorModel }
>(`/order/exportOrders`, async ({ startDate, endDate }: { startDate: string, endDate: string }, { rejectWithValue }: any) => {
    try {
        const response: any = await OrderService.exportOrders({ startDate, endDate });
        return response.data;
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});

export const createNewToteThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/order/createNewTote`,
    async (orderId, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.createNewTote(orderId);
            toast.success(response.data.message || 'New tote created successfully', { autoClose: 5000 });
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to create new tote', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const scanProductThunk = createAsyncThunk<ISuccessResponse, { body: ScanProductBody; toteId: string }, { rejectValue: IErrorModel }>(
    `/order/scanProduct`,
    async ({ body, toteId }: { body: ScanProductBody; toteId: string }, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.scanProduct(toteId, body);
            toast.success(response.data.message || 'Product scanned successfully', { autoClose: 5000 });
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to scan product', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const closeToteThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/order/closeTote`,
    async (toteId, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.closeTote(toteId);
            toast.success(response.data.message || 'Tote closed successfully', { autoClose: 5000 });
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to close tote', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const shipToteThunk = createAsyncThunk<ISuccessResponse, { toteId: number; customerNote?: string }, { rejectValue: IErrorModel }>(
    `/order/shipTote`,
    async ({ toteId, customerNote }, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.shipTote(toteId, customerNote);
            toast.success(response.data.message || 'Tote shipped successfully', { autoClose: 5000 });
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to ship tote', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const approveToteThunk = createAsyncThunk<ISuccessResponse, string, { rejectValue: IErrorModel }>(
    `/order/approveTote`,
    async (toteId, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.approveTote(toteId);
            toast.success(response.data.message || 'Tote approved successfully', { autoClose: 5000 });
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to approve tote', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const updateQuantityOrderThunk = createAsyncThunk<ISuccessResponse, { orderId: number; body: UpdateOrderQuantityBody[] }, { rejectValue: IErrorModel }>(
    `/order/updateQuantityOrder`,
    async ({ orderId, body }: { orderId: number; body: UpdateOrderQuantityBody[] }, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.updateQuantityOrder(orderId, body);
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to update quantity order', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const cancelOrderThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/order/cancelOrder`,
    async (orderId, { rejectWithValue }: any) => {
        try {
            const response: any = await OrderService.cancelOrder(orderId);
            toast.success(response.data.message || 'Order cancelled successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to cancel order', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const orderSlice = createSlice({
    name: `order`,
    initialState,
    reducers: {
        clearOrder(state) {
            state.order = null;
        },
    },
    extraReducers: (builder) => {
        // Place Order
        builder.addCase(placeOrderThunk.pending, (state) => {
            state.placeOrderLoading = true;
            state.loading = true;
        });
        builder.addCase(placeOrderThunk.fulfilled, (state) => {
            state.placeOrderLoading = false;
            state.loading = false;
        });
        builder.addCase(placeOrderThunk.rejected, (state, action) => {
            state.placeOrderLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Get Order By Id
        builder.addCase(getOrderByIdThunk.pending, (state) => {
            state.getOrderByIdLoading = true;
            state.loading = true;
        });
        builder.addCase(getOrderByIdThunk.fulfilled, (state: any, action: any) => {
            state.getOrderByIdLoading = false;
            state.loading = false;
            state.order = action.payload;
        });
        builder.addCase(getOrderByIdThunk.rejected, (state, action) => {
            state.getOrderByIdLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Filter Order
        builder.addCase(filterOrderThunk.pending, (state) => {
            state.filterOrderLoading = true;
            state.loading = true;
        });
        builder.addCase(filterOrderThunk.fulfilled, (state: any, action: any) => {
            state.filterOrderLoading = false;
            state.loading = false;
            // Handle Results wrapper in response
            const responseData = action.payload.Results || action.payload;
            state.orders = responseData.content || [];
            if (responseData.pageable) {
                state.pagination = {
                    pageNumber: responseData.pageable.pageNumber,
                    pageSize: responseData.pageable.pageSize,
                    totalPages: responseData.totalPages,
                    totalElements: responseData.totalElements,
                };
            }
        });
        builder.addCase(filterOrderThunk.rejected, (state, action) => {
            state.filterOrderLoading = false;
            state.loading = false;
            state.orders = [];
            state.error = action.payload;
        });

        // Search Order
        builder.addCase(searchOrderThunk.pending, (state) => {
            state.searchOrderLoading = true;
            state.loading = true;
        });
        builder.addCase(searchOrderThunk.fulfilled, (state, action) => {
            state.searchOrderLoading = false;
            state.loading = false;
            state.orders = action.payload.content || [];
            if (action.payload.pageable) {
                state.pagination = {
                    pageNumber: action.payload.pageable.pageNumber,
                    pageSize: action.payload.pageable.pageSize,
                    totalPages: action.payload.totalPages,
                    totalElements: action.payload.totalElements,
                };
            }
        });
        builder.addCase(searchOrderThunk.rejected, (state, action) => {
            state.searchOrderLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Export Orders
        builder.addCase(exportOrdersThunk.pending, (state) => {
            state.exportOrdersLoading = true;
            state.loading = true;
        });
        builder.addCase(exportOrdersThunk.fulfilled, (state) => {
            state.exportOrdersLoading = false;
            state.loading = false;
        });
        builder.addCase(exportOrdersThunk.rejected, (state, action) => {
            state.exportOrdersLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Create New Tote
        builder.addCase(createNewToteThunk.pending, (state) => {
            state.createNewToteLoading = true;
            state.loading = true;
        });
        builder.addCase(createNewToteThunk.fulfilled, (state) => {
            state.createNewToteLoading = false;
            state.loading = false;
        });
        builder.addCase(createNewToteThunk.rejected, (state, action) => {
            state.createNewToteLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Scan Product
        builder.addCase(scanProductThunk.pending, (state) => {
            state.scanProductLoading = true;
            state.loading = true;
        });
        builder.addCase(scanProductThunk.fulfilled, (state) => {
            state.scanProductLoading = false;
            state.loading = false;
        });
        builder.addCase(scanProductThunk.rejected, (state, action) => {
            state.scanProductLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Close Tote
        builder.addCase(closeToteThunk.pending, (state) => {
            state.closeToteLoading = true;
            state.loading = true;
        });
        builder.addCase(closeToteThunk.fulfilled, (state) => {
            state.closeToteLoading = false;
            state.loading = false;
        });
        builder.addCase(closeToteThunk.rejected, (state, action) => {
            state.closeToteLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Ship Tote
        builder.addCase(shipToteThunk.pending, (state) => {
            state.shipToteLoading = true;
            state.loading = true;
        });
        builder.addCase(shipToteThunk.fulfilled, (state) => {
            state.shipToteLoading = false;
            state.loading = false;
        });
        builder.addCase(shipToteThunk.rejected, (state, action) => {
            state.shipToteLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // Approve Tote
        builder.addCase(approveToteThunk.pending, (state) => {
            state.approveToteLoading = true;
            state.loading = true;
        });
        builder.addCase(approveToteThunk.fulfilled, (state) => {
            state.approveToteLoading = false;
            state.loading = false;
        });
        builder.addCase(approveToteThunk.rejected, (state, action) => {
            state.approveToteLoading = false;
            state.loading = false;
            state.error = action.payload;
        });
        // Update Quantity Order
        builder.addCase(updateQuantityOrderThunk.pending, (state) => {
            state.updateQuantityOrderLoading = true;
            state.loading = true;
        });
        builder.addCase(updateQuantityOrderThunk.fulfilled, (state) => {
            state.updateQuantityOrderLoading = false;
            state.loading = false;
        });
        builder.addCase(updateQuantityOrderThunk.rejected, (state, action) => {
            state.updateQuantityOrderLoading = false;
            state.loading = false;
            state.error = action.payload;
        });
        // Cancel Order
        builder.addCase(cancelOrderThunk.pending, (state) => {
            state.cancelOrderLoading = true;
            state.loading = true;
        });
        builder.addCase(cancelOrderThunk.fulfilled, (state) => {
            state.cancelOrderLoading = false;
            state.loading = false;
        });
        builder.addCase(cancelOrderThunk.rejected, (state, action) => {
            state.cancelOrderLoading = false;
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default orderSlice;
export const { clearOrder } = orderSlice.actions;
export const orderReducer = orderSlice.reducer;

